import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Clock, Search, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { apiCall } from "@/utils/api";
import { toast } from "sonner";

interface Course {
  id: number;
  idformation?: number;
  nom?: string;
  nomformation?: string;
}

interface HourBalance {
  email: string;
  formation_id: string;
  remaining_hours: number;
}

interface HourBalancesResponse {
  data: HourBalance[];
  current_page?: number;
  last_page?: number;
  total?: number;
}

interface Transaction {
  id: number;
  email: string;
  formation_id: string;
  hours: number;
  type: string;
  description: string | null;
  created_at: string;
}

interface StudentDetail {
  email: string;
  balances: { formation_id: string; remaining_hours: number }[];
  transactions: Transaction[];
}

const StudentHourCredits: React.FC = () => {
  const { token } = useAuth();

  // Data state
  const [courses, setCourses] = useState<Course[]>([]);
  const [balances, setBalances] = useState<HourBalance[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  // Filter state
  const [filterFormation, setFilterFormation] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Add hours dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    email: "",
    formation_id: "",
    hours: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Student detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Fetch courses on mount
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoadingCourses(true);
      try {
        const response: any = await apiCall(token, "/learnybox/courses");
        let data = response;
        if (response.data) data = response.data;
        if (!Array.isArray(data) && Array.isArray(response)) data = response;
        setCourses(data);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Erreur lors du chargement des formations");
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [token]);

  // Fetch balances on mount
  useEffect(() => {
    if (!token) return;
    fetchBalances();
  }, [token]);

  const fetchBalances = async () => {
    setIsLoadingBalances(true);
    try {
      const response = await apiCall<HourBalancesResponse | HourBalance[]>(
        token,
        "/student-hours?per_page=9999"
      );

      if (Array.isArray(response)) {
        setBalances(response);
      } else if (response.data && Array.isArray(response.data)) {
        setBalances(response.data);
      } else {
        setBalances([]);
      }
    } catch (error) {
      console.error("Error fetching balances:", error);
      toast.error("Erreur lors du chargement des soldes");
      setBalances([]);
    } finally {
      setIsLoadingBalances(false);
    }
  };

  // Filtered balances
  const filteredBalances = useMemo(() => {
    return balances.filter((b) => {
      const matchesFormation =
        filterFormation === "all" || b.formation_id === filterFormation;
      const matchesSearch =
        !searchTerm || b.email.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFormation && matchesSearch;
    });
  }, [balances, filterFormation, searchTerm]);

  // Get course name by formation_id
  const getCourseName = (formationId: string): string => {
    const course = courses.find(
      (c) =>
        String(c.idformation || c.id) === String(formationId)
    );
    return course?.nomformation || course?.nom || `Formation ${formationId}`;
  };

  // Hours badge color
  const getHoursBadge = (hours: number) => {
    if (hours > 5) {
      return (
        <Badge className="bg-emerald-50 text-emerald-600 border-none shadow-sm shadow-emerald-200/50 text-[10px] font-semibold px-3 py-1 rounded-xl">
          {hours}h
        </Badge>
      );
    }
    if (hours >= 1) {
      return (
        <Badge className="bg-amber-50 text-amber-600 border-none shadow-sm shadow-amber-200/50 text-[10px] font-semibold px-3 py-1 rounded-xl">
          {hours}h
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-50 text-red-600 border-none shadow-sm shadow-red-200/50 text-[10px] font-semibold px-3 py-1 rounded-xl">
        {hours}h
      </Badge>
    );
  };

  // Add hours handler
  const handleAddHours = async () => {
    if (!addForm.email || !addForm.formation_id || !addForm.hours) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await apiCall<{ success: boolean; message: string; data?: any }>(
        token,
        "/student-hours",
        "POST",
        {
          email: addForm.email,
          formation_id: addForm.formation_id,
          hours: Number(addForm.hours),
          description: addForm.description || undefined,
        }
      );

      if (result.success === false) {
        toast.error("Erreur", { description: result.message });
      } else {
        toast.success("Heures ajoutées", { description: result.message || "Les heures ont été ajoutées avec succès." });
        setAddDialogOpen(false);
        setAddForm({ email: "", formation_id: "", hours: "", description: "" });
        await fetchBalances();
      }
    } catch (error: any) {
      console.error("Error adding hours:", error);
      toast.error("Erreur", { description: error.message || "Impossible d'ajouter les heures." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Student detail handler
  const handleRowClick = async (email: string) => {
    setDetailDialogOpen(true);
    setIsLoadingDetail(true);
    setSelectedStudent(null);
    try {
      const result = await apiCall<StudentDetail>(
        token,
        `/student-hours/${encodeURIComponent(email)}`
      );
      setSelectedStudent(result);
    } catch (error) {
      console.error("Error fetching student detail:", error);
      toast.error("Erreur lors du chargement du détail");
      setDetailDialogOpen(false);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-2xl">
              <Clock className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">
              Heures de suivi
              {filteredBalances.length > 0 && (
                <span className="ml-3 text-2xl text-slate-300">({filteredBalances.length})</span>
              )}
            </h1>
          </div>
          <p className="text-slate-500 font-medium ml-14">
            Gérez les crédits d'heures de suivi des étudiants par formation.
          </p>
        </div>
        <div className="flex items-center gap-3 ml-14 md:ml-0">
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="rounded-2xl h-12 px-6 font-bold shadow-xl shadow-primary/20 transition-all hover:scale-105 active:scale-95"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter des heures
          </Button>
        </div>
      </div>

      {/* Filters & Table */}
      <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl">
        <CardHeader className="p-8 pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1 max-w-xl">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Formation
                </label>
                {isLoadingCourses ? (
                  <div className="flex items-center space-x-2 text-sm text-slate-500 h-12 border border-slate-100 rounded-2xl px-4 bg-slate-50/50">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="font-bold italic uppercase tracking-widest text-[10px]">
                      Chargement des formations...
                    </span>
                  </div>
                ) : (
                  <SearchableSelect
                    value={filterFormation}
                    onValueChange={setFilterFormation}
                    placeholder="Toutes les formations"
                    className="w-full h-12 rounded-2xl bg-slate-50 border-slate-100 focus:ring-primary/20 font-bold transition-all"
                    options={[
                      { value: "all", label: "Toutes les formations" },
                      ...courses.map((course: any) => ({
                        value: String(course.idformation || course.id),
                        label: course.nomformation || course.nom || `Formation ${course.idformation}`,
                      })),
                    ]}
                  />
                )}
              </div>
            </div>

            <div className="flex-1 max-w-md">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Recherche
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par email..."
                    className="pl-12 h-12 bg-slate-50 border-slate-100 rounded-2xl focus:ring-primary/20 font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-50 hover:bg-transparent">
                <TableHead className="w-[60px] pl-8 text-[11px] font-black uppercase tracking-widest text-slate-400">
                  #
                </TableHead>
                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  Email
                </TableHead>
                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  Formation
                </TableHead>
                <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  Heures restantes
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingBalances ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-64 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm font-bold text-slate-400 italic uppercase tracking-widest">
                        Chargement des soldes...
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredBalances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-64 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <Clock className="h-12 w-12 opacity-10" />
                      <p className="text-sm font-bold italic uppercase tracking-widest">
                        {searchTerm || filterFormation !== "all"
                          ? "Aucun résultat trouvé"
                          : "Aucun solde enregistré"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBalances.map((balance, index) => (
                  <TableRow
                    key={`${balance.email}-${balance.formation_id}`}
                    className="group border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer"
                    onClick={() => handleRowClick(balance.email)}
                  >
                    <TableCell className="pl-8 py-5">
                      <span className="text-xs font-black text-slate-300">
                        {(index + 1).toString().padStart(2, "0")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors tracking-tight">
                        {balance.email}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-semibold text-slate-500">
                        {getCourseName(balance.formation_id)}
                      </span>
                    </TableCell>
                    <TableCell>{getHoursBadge(balance.remaining_hours)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Hours Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter des heures</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-email">Email</Label>
              <Input
                id="add-email"
                type="email"
                placeholder="etudiant@example.com"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-formation">Formation</Label>
              <SearchableSelect
                value={addForm.formation_id}
                onValueChange={(val) => setAddForm({ ...addForm, formation_id: val })}
                placeholder="Sélectionner une formation"
                options={courses.map((course: any) => ({
                  value: String(course.idformation || course.id),
                  label: course.nomformation || course.nom || `Formation ${course.idformation}`,
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-hours">Nombre d'heures</Label>
              <Input
                id="add-hours"
                type="number"
                placeholder="Ex: 10"
                value={addForm.hours}
                onChange={(e) => setAddForm({ ...addForm, hours: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-description">Description</Label>
              <Input
                id="add-description"
                placeholder="Motif de l'ajout (optionnel)"
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddHours} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedStudent ? selectedStudent.email : "Chargement..."}
            </DialogTitle>
          </DialogHeader>

          {isLoadingDetail ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-bold text-slate-400 italic uppercase tracking-widest">
                Chargement...
              </p>
            </div>
          ) : selectedStudent ? (
            <div className="space-y-6">
              {/* Balances per formation */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">
                  Soldes par formation
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedStudent.balances.map((b) => (
                    <div
                      key={b.formation_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <span className="text-sm font-semibold text-slate-700 truncate mr-2">
                        {getCourseName(b.formation_id)}
                      </span>
                      {getHoursBadge(b.remaining_hours)}
                    </div>
                  ))}
                  {selectedStudent.balances.length === 0 && (
                    <p className="text-sm text-slate-400 italic col-span-2">
                      Aucun solde enregistré.
                    </p>
                  )}
                </div>
              </div>

              {/* Transaction history */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">
                  Historique des transactions
                </h3>
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-50 hover:bg-transparent">
                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Date
                        </TableHead>
                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Formation
                        </TableHead>
                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Type
                        </TableHead>
                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Heures
                        </TableHead>
                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Description
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedStudent.transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-20 text-center text-slate-400">
                            <p className="text-sm italic">Aucune transaction.</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedStudent.transactions.map((tx) => (
                          <TableRow key={tx.id} className="border-slate-50">
                            <TableCell className="text-xs font-bold text-slate-400 whitespace-nowrap">
                              {new Date(tx.created_at).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-slate-500">
                              {getCourseName(tx.formation_id)}
                            </TableCell>
                            <TableCell>
                              <Badge className={cn(
                                "text-[10px] font-semibold px-2 py-0.5 rounded-lg border-none",
                                tx.type === "purchase" && "bg-emerald-50 text-emerald-600",
                                tx.type === "consumption" && "bg-blue-50 text-blue-600",
                                tx.type === "adjustment" && "bg-amber-50 text-amber-600",
                                tx.type === "refund" && "bg-purple-50 text-purple-600",
                              )}>
                                {{ purchase: "Achat", consumption: "Consommation", adjustment: "Ajustement", refund: "Remboursement" }[tx.type] || tx.type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span
                                className={cn(
                                  "text-sm font-black",
                                  tx.hours > 0 ? "text-emerald-600" : "text-red-600"
                                )}
                              >
                                {tx.hours > 0 ? `+${tx.hours}` : tx.hours}h
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {tx.description || "-"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentHourCredits;
